import { signUpFormSchema } from "@rmnlng/shared";
import { useForm } from "@tanstack/react-form";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { FieldError } from "@/components/FieldError";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useMe, useRegister } from "@/lib/auth";
import { useErrorMessage } from "@/lib/error-message";
import { useT } from "@/lib/i18n";

/**
 * Self-service signup. Mirrors the LoginPage layout exactly — same logo
 * placement, same single-column max-width, same field styling. Only the
 * differences (third field for confirm, link back to /login at the
 * bottom) deviate.
 *
 * On success the server returns the same `LoginResponse` shape as
 * login, so `useRegister`'s onSuccess seeds the auth cache identically
 * — the new user is signed in and we just navigate home.
 *
 * Validation: client-side Zod against `signUpSchema` (email format,
 * password ≥ 8 chars, confirmPassword must match). Server runs the
 * same schema, so the only network-only error class is duplicate-email
 * (HTTP 409 → "email already registered" copy).
 */
export function SignUpPage() {
  const { data: user } = useMe();
  const register = useRegister();
  const navigate = useNavigate();
  const t = useT();
  const errorMessage = useErrorMessage();

  const form = useForm({
    defaultValues: { name: "", email: "", password: "", confirmPassword: "" },
    validators: { onChange: signUpFormSchema },
    onSubmit: async ({ value }) => {
      await register.mutateAsync(value);
      // Always land on home after signup — there's no "from" location to
      // restore (login routes through ProtectedRoute, signup is reached
      // by direct intent so it has no redirect history to preserve).
      navigate("/", { replace: true });
    },
  });

  if (user) return <Navigate to="/" replace />;

  return (
    <div className="flex min-h-dvh w-full items-stretch justify-center bg-bg">
      <div className="flex w-full max-w-[440px] flex-col px-7 pt-12 pb-8">
        <div className="mb-12">
          <Logo className="size-12" />
        </div>

        <h1 className="mb-2.5 text-[32px] font-bold leading-[1.1] tracking-[-0.03em]">{t("signup.title")}</h1>
        <p className="mb-9 text-[15px] leading-[1.5] text-text-3">{t("signup.subtitle")}</p>

        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
          noValidate
        >
          <form.Field name="name">
            {(field) => (
              <div>
                <label htmlFor={field.name} className="mb-1.5 block text-[12px] font-medium text-text-2">
                  {t("signup.name")}
                </label>
                <Input
                  id={field.name}
                  type="text"
                  // `name` triggers some browsers' autofill on a saved
                  // contact entry — this is a NEW account though, so
                  // we don't want it pre-filled with system data.
                  // `off` is the strongest signal we have.
                  autoComplete="off"
                  placeholder={t("signup.name_placeholder")}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
                <FieldError errors={field.state.meta.errors} touched={field.state.meta.isTouched} />
              </div>
            )}
          </form.Field>

          <form.Field name="email">
            {(field) => (
              <div>
                <label htmlFor={field.name} className="mb-1.5 block text-[12px] font-medium text-text-2">
                  {t("signup.email")}
                </label>
                <Input
                  id={field.name}
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
                <FieldError errors={field.state.meta.errors} touched={field.state.meta.isTouched} />
              </div>
            )}
          </form.Field>

          <form.Field name="password">
            {(field) => (
              <div>
                <label htmlFor={field.name} className="mb-1.5 block text-[12px] font-medium text-text-2">
                  {t("signup.password")}
                </label>
                <Input
                  id={field.name}
                  type="password"
                  // `new-password` so password managers offer to generate +
                  // store rather than autofill an existing credential. The
                  // confirm field below uses the same value to match.
                  autoComplete="new-password"
                  placeholder={t("signup.password_placeholder")}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
                <FieldError errors={field.state.meta.errors} touched={field.state.meta.isTouched} />
              </div>
            )}
          </form.Field>

          <form.Field name="confirmPassword">
            {(field) => (
              <div>
                <label htmlFor={field.name} className="mb-1.5 block text-[12px] font-medium text-text-2">
                  {t("signup.confirm_password")}
                </label>
                <Input
                  id={field.name}
                  type="password"
                  autoComplete="new-password"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
                <FieldError errors={field.state.meta.errors} touched={field.state.meta.isTouched} />
              </div>
            )}
          </form.Field>

          {register.error && (
            <p className="text-[13px] text-[color:var(--again)]" role="alert">
              {errorMessage(register.error)}
            </p>
          )}

          <form.Subscribe
            selector={(s) => ({
              canSubmit: s.canSubmit,
              isSubmitting: s.isSubmitting,
            })}
          >
            {({ canSubmit, isSubmitting }) => (
              <Button type="submit" size="block" className="mt-3" disabled={!canSubmit || isSubmitting}>
                {isSubmitting ? t("signup.submitting") : t("signup.submit")}
              </Button>
            )}
          </form.Subscribe>
        </form>

        <div className="mt-auto pt-6 text-center text-[13px] text-text-3">
          {t("signup.have_account")}{" "}
          <Link to="/login" className="font-medium text-[color:var(--accent)]">
            {t("signup.sign_in")}
          </Link>
        </div>
      </div>
    </div>
  );
}
